import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import qs from 'querystring';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import moment from 'moment';
import { MANUAL_PAYMENT_METHODS } from 'config/payment-type';
import { ROLES } from 'config/roles';
import { INITIAL_DATE_RANGE } from 'config/giving-history';
import { logger, utils } from 'service';
import {
  DatePicker,
  DropDownMenu,
  ExcelDownLoad,
  Loading,
  RoundedTable,
  Scrollbar,
  UserAvatar,
} from 'components';
import { fetchGivingHistoryRequest } from 'redux/actions/GiftActions';
import { fetchUsersRequest } from 'redux/actions/UsersActions';
import GivingHistoryImage from 'assets/images/givinghistory.png';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import 'pages/GivingHistory/style.css';
import './style.css';

class GivingHistoryDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedProductID: 'all',
      selectedPaymentMethod: 'all',
      giftSummary: {
        history: [],
        productTotalMap: {},
        total: 0,
        isLoading: false,
      },
    };
    this.user = null;
  }

  componentDidMount() {
    this.loadData(false);
  }

  componentDidUpdate(prevProps) {
    const {
      from: prevFrom,
      to: prevTo,
      userID: prevUserID,
      gifts: prevGifts,
      users: prevUsers,
    } = prevProps;
    const { userID, from, to, gifts, users } = this.props;
    if (
      prevFrom !== from ||
      prevTo !== to ||
      prevUserID !== userID ||
      JSON.stringify(gifts) !== JSON.stringify(prevGifts) ||
      JSON.stringify(users) !== JSON.stringify(prevUsers)
    ) {
      this.loadData(false);
    }
  }

  goBack = () => {
    const { history, location, from, to } = this.props;
    if (location.state && location.state.from) {
      history.goBack();
    } else {
      history.replace(`/dashboard/givings?from=${from}&to=${to}`, {
        from: location.state ? location.state.from : undefined,
      });
    }
  };

  loadData = isRefresh => {
    const { selectedProductID, selectedPaymentMethod, giftSummary } = this.state;
    const { history, gifts, dispatch, churchID, users, location, from, to, userID } = this.props;
    this.setState({ giftSummary: { ...giftSummary, isLoading: true } }, async () => {
      if (users.isLoading || gifts.isLoading) return;
      try {
        let gHistory;
        const productTotalMap = {};
        if (isRefresh || gifts.from > from || gifts.to < to) {
          const [hist] = await Promise.all([
            dispatch(fetchGivingHistoryRequest(churchID, from, to)),
            dispatch(fetchUsersRequest(churchID)),
          ]);
          gHistory = hist;
        } else {
          gHistory = [...gifts.history];
        }
        const user = users.data.find(u => userID === u.id);
        if (!user) {
          history.replace(`/dashboard/givings?from=${from}to=${to}`, {
            from: location.state ? location.state.from : undefined,
          });
          return;
        }
        this.user = user;
        if (gHistory && gHistory.length) {
          gHistory.forEach(item => {
            productTotalMap[item.productID] = (productTotalMap[item.productID] || 0) + item.total / 100;
          });
        }
        gHistory = gHistory.filter(
          item =>
            item.date >= from &&
            item.date <= to &&
            item.userID === user.id &&
            (selectedProductID === 'all' || item.productID === selectedProductID) &&
            (selectedPaymentMethod === 'all' ||
              (selectedPaymentMethod === 'manual' && item.giftID) ||
              selectedPaymentMethod === item.methodType),
        );
        let total = 0;
        gHistory.forEach(item => {
          total += item.total / 100;
        });

        this.setState({
          giftSummary: {
            history: gHistory.map(gh => ({
              ...gh,
              productName: (gifts.data.find(g => g.productID === gh.productID) || { title: 'No Title' })
                .title,
            })),
            productTotalMap,
            total,
            isLoading: false,
          },
        });
        return;
      } catch (error) {
        logger.warn('loadData in GivingHistoryDetail', error);
      }
      this.setState({
        giftSummary: {
          history: [],
          productTotalMap: {},
          total: 0,
          isLoading: false,
        },
      });
    });
  };

  handleChangeFilter = (name, value) => {
    const { history, location, userID, from, to } = this.props;
    if (name === 'from' || name === 'to') {
      const dateFrom = name === 'from' ? moment(value.getTime()).startOf('day').valueOf() : from;
      const dateTo = name === 'to' ? moment(value.getTime()).endOf('day').valueOf() : to;
      history.replace(`/dashboard/giving/${userID}?from=${dateFrom}&to=${dateTo}`, {
        from: location.state ? location.state.from : undefined,
      });
    } else {
      this.setState({ [name]: value }, () => {
        this.loadData(false);
      });
    }
  };

  handleEditGift = () => {
    const { history, location } = this.props;
    history.replace('/dashboard/settings/gift_settings', {
      from: location.state ? location.state.from : undefined,
    });
  };

  handleManualEntryUpload = () => {
    const { history, location, userID } = this.props;
    history.push(`/dashboard/givings/upload/${userID}`, {
      from: location.pathname,
    });
  };

  render() {
    const { giftSummary, selectedProductID, selectedPaymentMethod } = this.state;
    const { gifts, authUserPermissions, from, to } = this.props;
    const isGiftEditor =
      authUserPermissions.isSuperUser ||
      !!authUserPermissions.permissions.find(pm => pm === ROLES.GIFT_EDITOR.value);
    const renderGiftHistoryByProduct = gHistory => {
      if (!gHistory || !gHistory.length) return <div />;
      const reducer = (accumulator, currentValue) => ({
        total: accumulator.total + currentValue.total,
      });
      const historyObj = utils.groupByProductID(giftSummary.history);
      return Object.keys(historyObj)
        .map(key => {
          const gift = gifts.data.find(g => g.productID === key) || { title: 'No Title' };
          const { total } = (historyObj[key] || []).reduce(reducer, { total: 0 });
          return {
            gift,
            history: historyObj[key] || [],
            total,
          };
        })
        .sort((a, b) => {
          if (a.gift.title === 'No Title') return 1;
          if (b.gift.title === 'No Title') return -1;
          return b.total - a.total;
        })
        .map((obj, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <li key={index}>
            <div className="g-showgiving-detail">
              <p>{`${obj.gift.title}`}</p>
              <span className={obj.total < 0 ? 'color-danger' : ''}>
                {utils.formatValue(obj.total / 100, 'currency')}
              </span>
            </div>
          </li>
        ));
    };
    return (
      <>
        <Helmet title="Giving history details" />
        <div className="giving-history card-wrapper customWidth">
          <div className="back">
            <ChevronLeftIcon />
            <button type="button" onClick={this.goBack}>
              Back
            </button>
          </div>
          <div className="card">
            <div className="card-header">
              <div className="title">
                <img src={GivingHistoryImage} alt="Giving History" />
                <p style={{ whiteSpace: 'normal' }}>
                  {this.user ? (
                    <>
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>Giving history</span>
                      &nbsp;-&nbsp;
                      <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>
                        {this.user.profile.firstName} {this.user.profile.lastName}
                      </span>
                    </>
                  ) : (
                    'Giving history'
                  )}
                </p>
              </div>
              <div className="title-buttons">
                <button
                  type="button"
                  className="button neutral border--red bg-white medium"
                  onClick={this.handleManualEntryUpload}
                >
                  Manual Entry
                </button>
                {isGiftEditor && (
                  <button type="button" className="button gradient medium" onClick={this.handleEditGift}>
                    Edit Gift Options
                  </button>
                )}
                <ExcelDownLoad
                  table="user-ghistory-detail-table-to-xls"
                  filename={
                    this.user
                      ? `Giving history - ${this.user.profile.firstName} ${this.user.profile.lastName}`
                      : 'Giving history'
                  }
                  sheet={`${utils.formatValue(from, 'date')} - ${utils.formatValue(to, 'date')}`}
                  buttonText="Export To Excel"
                  className="button gradient medium"
                  disabled={giftSummary.isLoading}
                />
                {!giftSummary.isLoading && (
                  <table hidden id="user-ghistory-detail-table-to-xls">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Giving Type</th>
                        <th>Payment Method</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {giftSummary.history.map((gh, index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <tr key={index}>
                          <td>{utils.formatValue(gh.date, 'date')}</td>
                          <td>
                            {
                              (gifts.data.find(g => g.productID === gh.productID) || { title: 'No Title' })
                                .title
                            }
                          </td>
                          <td>
                            {
                              (
                                [{ label: 'Faithmo', value: 'faithmo' }, ...MANUAL_PAYMENT_METHODS].find(
                                  g => g.value === gh.methodType,
                                ) || { label: null }
                              ).label
                            }
                          </td>
                          <td>{utils.formatValue(gh.total / 100, 'currency')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
            <div className="card-block g-main-detail-history">
              <div className="row mb-3">
                <div className="col-md-10 col-sm-12">
                  <div className="row">
                    <div className="col-lg-6 col-md-12">
                      <div className="row">
                        <div className="col-md-6 col-sm-12">
                          <div className="g-from g-picker-container">
                            <span className="label">From: </span>
                            <DatePicker
                              clearIcon={null}
                              onChange={value => this.handleChangeFilter('from', value)}
                              value={new Date(from)}
                            />
                          </div>
                        </div>
                        <div className="col-md-6 col-sm-12">
                          <div className="g-to g-picker-container">
                            <span className="label">To: </span>
                            <DatePicker
                              clearIcon={null}
                              onChange={value => this.handleChangeFilter('to', value)}
                              value={new Date(to)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-6 col-md-12">
                      <div className="row">
                        <div className="col-md-6 col-sm-12">
                          <div className="g-picker-container">
                            <span className="label">Gift Options: </span>
                            <DropDownMenu
                              value={selectedProductID}
                              onChange={e => this.handleChangeFilter('selectedProductID', e.target.value)}
                              menuArray={[{ title: 'All', productID: 'all' }, ...(gifts.data || [])]
                                .filter(
                                  gift =>
                                    gift.productID === 'all' || giftSummary.productTotalMap[gift.productID],
                                )
                                .map(item => ({
                                  label: item.title,
                                  value: item.productID,
                                }))
                                .sort((a, b) => {
                                  if (a.value === 'all') return -1;
                                  if (b.value === 'all') return 1;
                                  return a.label.localeCompare(b.label);
                                })}
                              style={{ margin: 0 }}
                            />
                          </div>
                        </div>
                        <div className="col-md-6 col-sm-12">
                          <div className="g-picker-container">
                            <span className="label">Payment Method: </span>
                            <DropDownMenu
                              value={selectedPaymentMethod}
                              onChange={e =>
                                this.handleChangeFilter('selectedPaymentMethod', e.target.value)
                              }
                              menuArray={[
                                { label: 'All', value: 'all' },
                                { label: 'Faithmo', value: 'faithmo' },
                                { label: 'Manual', value: 'manual' },
                                ...MANUAL_PAYMENT_METHODS,
                              ]}
                              style={{ margin: 0 }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  className="col-md-2 col-sm-12"
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    paddingTop: 12,
                  }}
                >
                  <button
                    type="button"
                    className="button neutral border--red small"
                    onClick={() => this.loadData(true)}
                  >
                    Reload
                  </button>
                </div>
              </div>
              {giftSummary.isLoading ? (
                <div
                  style={{
                    position: 'relative',
                    minHeight: '120px',
                    minWidth: '120px',
                  }}
                >
                  <Loading />
                </div>
              ) : (
                <div className="row">
                  <div className="col-lg-8 col-md-12 g-main-history-table">
                    <RoundedTable
                      // showPagenate
                      showCount
                      minWidth={720}
                      data={giftSummary.history.map(gh => [
                        <div>{utils.formatValue(gh.date, 'date')}</div>,
                        <div>
                          {
                            (gifts.data.find(g => g.productID === gh.productID) || { title: 'No Title' })
                              .title
                          }
                        </div>,
                        <div>
                          {
                            (
                              [{ label: 'Faithmo', value: 'faithmo' }, ...MANUAL_PAYMENT_METHODS].find(
                                g => g.value === gh.methodType,
                              ) || { label: null }
                            ).label
                          }
                        </div>,
                        <div className={gh.total < 0 ? 'amount color-danger' : 'amount'}>
                          {utils.formatValue(gh.total / 100, 'currency')}
                        </div>,
                      ])}
                      headings={[
                        { component: <div>Date</div>, percentWidth: 20 },
                        { component: <div>Giving Type</div>, percentWidth: 30 },
                        { component: <div>Payment Method</div>, percentWidth: 30 },
                        {
                          component: <div>Amount</div>,
                          percentWidth: 20,
                        },
                      ]}
                    />
                  </div>
                  <div className="col-lg-4 col-md-12 g-total-by">
                    {this.user ? (
                      <UserAvatar user={this.user} style={{ margin: 24, marginTop: 0 }} />
                    ) : (
                      <div
                        style={{
                          position: 'relative',
                          minHeight: '60px',
                          minWidth: '60px',
                        }}
                      >
                        <Loading />
                      </div>
                    )}
                    <div className="g-total-by-date">
                      <div className="g-total-by-date-top">
                        <p>
                          <span>Showing Givings From</span>
                          &nbsp;
                          <span>{utils.formatValue(from, 'date')}</span>
                          &nbsp;-&nbsp;
                          <span>{utils.formatValue(to, 'date')}</span>
                        </p>
                      </div>
                      <div className="g-total-by-date-main">
                        <Scrollbar autoHeight autoHeightMin={336} autoHeightMax={576}>
                          <ul>{renderGiftHistoryByProduct(giftSummary.history)}</ul>
                        </Scrollbar>
                      </div>
                      <div className="g-total-by-date-bottom">
                        <span>Grand Total:</span>
                        <span className={giftSummary.total < 0 ? 'color-danger' : ''}>
                          {utils.formatValue(giftSummary.total, 'currency')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

GivingHistoryDetail.propTypes = {
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  churchID: PropTypes.string.isRequired,
  userID: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  gifts: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        churchID: PropTypes.string.isRequired,
        created: PropTypes.number.isRequired,
        productID: PropTypes.string.isRequired,
        status: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
      }),
    ),
    history: PropTypes.arrayOf(
      PropTypes.shape({
        methodType: PropTypes.string.isRequired,
        churchID: PropTypes.string.isRequired,
        customerID: PropTypes.string,
        userID: PropTypes.string.isRequired,
        productID: PropTypes.string.isRequired,
        date: PropTypes.number.isRequired,
        created: PropTypes.number.isRequired,
        total: PropTypes.number.isRequired,
        desc: PropTypes.string,
        giftID: PropTypes.string,
      }),
    ),
    from: PropTypes.number.isRequired,
    to: PropTypes.number.isRequired,
    isLoading: PropTypes.number.isRequired,
  }).isRequired,
  users: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        email: PropTypes.string,
        profile: PropTypes.object.isRequired,
      }),
    ),
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  authUserPermissions: PropTypes.shape({
    permissions: PropTypes.arrayOf(PropTypes.string.isRequired),
    isSuperUser: PropTypes.bool.isRequired,
  }).isRequired,
  from: PropTypes.number,
  to: PropTypes.number,
};
GivingHistoryDetail.defaultProps = {
  from: INITIAL_DATE_RANGE.from,
  to: INITIAL_DATE_RANGE.to,
};
// Retrieve data from store as props
const mapStateToProps = ({ auth: { user }, users, gifts, church }, props) => {
  let from;
  let to;
  if (props.location.search && props.location.search.length > 1) {
    try {
      const queryObj = qs.parse(props.location.search.substr(1), '&', '=');
      from = parseInt(queryObj.from, 10);
      to = parseInt(queryObj.to, 10);
    } catch (error) {
      // continue regardless of error
    }
  }
  return {
    users,
    gifts,
    churchID: church.id,
    authUserPermissions: church.admins[user.id],
    userID: props.match.params.userId,
    from,
    to,
  };
};

export default withRouter(connect(mapStateToProps)(GivingHistoryDetail));
